import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Html5Qrcode, Html5QrcodeScanner } from "html5-qrcode";
import { Image as ImageIcon, ScanLine } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const REGION_ID = "okn-qr-region";

const safeClear = (instance) => {
  try {
    const result = instance?.clear();
    if (result && typeof result.catch === "function")
      result.catch((error) => console.debug("QR scanner cleanup", error));
  } catch (error) {
    // html5-qrcode throws when it was never started.
    console.debug("QR scanner cleanup", error);
  }
};

/** Reads a pallet QR with the device camera (or from an uploaded photo on desktop). */
export function QrScannerDialog({ open, onOpenChange, onResult }) {
  const scannerRef = useRef(null);
  const handlersRef = useRef({ onResult, onOpenChange });
  const [mounted, setMounted] = useState(false);
  const [ready, setReady] = useState(false);

  handlersRef.current = { onResult, onOpenChange };

  // The region div only exists once Radix has mounted the dialog content.
  const regionRef = useCallback((node) => setMounted(Boolean(node)), []);

  useEffect(() => {
    if (!open || !mounted || scannerRef.current) return;
    if (!document.getElementById(REGION_ID)) return;
    const scanner = new Html5QrcodeScanner(
      REGION_ID,
      {
        fps: 10,
        qrbox: { width: 240, height: 240 },
        rememberLastUsedCamera: true,
        showTorchButtonIfSupported: true,
      },
      false
    );
    scannerRef.current = scanner;
    scanner.render(
      (text) => {
        // The dialog stays open when the code is not a pallet label.
        if (handlersRef.current.onResult(text) !== false)
          handlersRef.current.onOpenChange(false);
      },
      () => {}
    );
    setReady(true);
    return () => {
      setReady(false);
      safeClear(scannerRef.current);
      scannerRef.current = null;
    };
  }, [open, mounted]);

  const scanFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new Html5Qrcode(`${REGION_ID}-file`);
    try {
      const text = await reader.scanFile(file, true);
      if (handlersRef.current.onResult(text) !== false)
        handlersRef.current.onOpenChange(false);
    } catch {
      toast.error("No QR code found in that image");
    } finally {
      safeClear(reader);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="qr-scanner-dialog"
        className="max-w-md border-slate-200 bg-white text-slate-800"
      >
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2 text-lg font-medium tracking-tight">
            <span
              className="okn-scan text-cyan-600"
              style={{ "--okn-scan-travel": "10px" }}
              data-testid="qr-dialog-scan-animation"
            >
              <ScanLine size={17} />
            </span>{" "}
            Scan pallet QR
          </DialogTitle>
        </DialogHeader>
        <div id={REGION_ID} ref={regionRef} className="overflow-hidden rounded-xl" />
        <div id={`${REGION_ID}-file`} className="hidden" />
        {!ready ? (
          <p className="text-[11.5px] text-slate-500">Starting the camera…</p>
        ) : null}
        <label className="mono flex cursor-pointer items-center justify-center gap-2 rounded-full border border-slate-200 py-2 text-[10.5px] uppercase tracking-wider text-slate-600 hover:border-cyan-400 hover:text-cyan-700">
          <ImageIcon size={13} /> Scan from a photo
          <input
            data-testid="qr-file-input"
            type="file"
            accept="image/*"
            onChange={scanFile}
            className="hidden"
          />
        </label>
        <p className="text-[10.5px] leading-relaxed text-slate-500">
          Camera access needs HTTPS and your permission. On a phone or tablet pick the rear camera
          for the best result.
        </p>
      </DialogContent>
    </Dialog>
  );
}
